import React, { useRef } from 'react';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Name, Title, Code, Actions, API, Utility, Button, Messages, Control } from 'helpers/PrintImports';
import { Client } from 'models/ClientModel';
import { Company } from 'models/CompanyModel';
import { Location } from 'models/LocationModel';
import { Insurance } from 'models/InsuranceModel';
import SignaturePad from "components/common/SignManager";
import { data, useSearchParams } from 'react-router-dom';

const ConsentPrint17: React.FC = () => {
  const titleName = 'Sun Life';
  const [formId, formName] = [Code.Form.Id.CLIENT, Code.Form.Name.CLIENT];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [clientData, setClientData] = useState<Client>({});
  const [signData, setSignData] = useState<string>('');
  const [receiverName, setReceiverName] = useState<string>('');
  const [receiverPhone, setReceiverPhone] = useState<string>('');
  const [memberId, setMemberId] = useState<string>('');
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [groupNumber, setGroupNumber] = useState<string>('');
  const [insurerName, setInsurerName] = useState<string>('');
  const [primaryHolder, setPrimaryHolder] = useState<string>('');
  const [secondaryHolder, setSecondaryHolder] = useState<string>('');
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [locationData, setLocationData] = useState<Location | null>(null);
  const [insuranceData, setInsuranceData] = useState<Insurance[]>([]);

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const today = Utility.Date.Current();

  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);
  const companyName = Utility.Storage.CompanyName();
  const companyId = Utility.Storage.CompanyId();
  const locationId = Utility.Storage.LocationId();
  const locateDate = Utility.Date.getLocalDate();

  useEffect(() => {
    if(!clientId) return;
    API.Data.GetByKeyId<Client>(formName, clientId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setClientData((prevData) => ({
        ...prevData,
        ...formData,
      }));
      const company = await API.Data.GetCompany(companyId);
      if(company){ setCompanyData(company); }
      const location = await API.Data.GetLocationCity(locationId);      
      if(location){ setLocationData(location); }
      setReceiverName(Utility.Get.FullName(data,Code.Form.Name.CLIENT));
      setReceiverPhone(data.client_phone || '');
      callInsuranceList(data.client_id);
    }})
    setLoading(false);
  }, []); 

  const callInsuranceList = async (clientId: number | undefined) => {
      if (!clientId) return;  
      const url = API.Build.FormApiEndpint(Code.Form.Name.INSURANCE, 1, Name.Insurance.Id, Code.Option.Sort.DESC, {
        fields: Utility.Join.Fields([
          Name.Insurance.Id,
          Name.Insurance.PolicyNumber, 
          Name.Insurance.memberNumber, 
          Name.Insurance.GroupNumber, 
          Name.Insurance.PrimaryHolderName, 
          Name.Insurance.SecondaryHolderName, 
          Name.Insurance.PrimaryRelationship, 
          Name.Insurance.SecondaryRelationship, 
          Name.Insurer.Name,
        ]),
        [Name.Client.Id]: clientId,
      });
      await API.Data.List<Insurance>(url, setInsuranceData, setError, setLoading);
    };
  
    useEffect(() => {
    if (insuranceData && insuranceData.length > 0) {
      const firstRow = insuranceData[0];
      setPolicyNumber(firstRow.insurance_policy_number || '');
      setMemberId(firstRow.insurance_member_number || '');
      setGroupNumber(firstRow.insurance_group_number || '');
      setInsurerName(firstRow.insurer_name || '');
      setPrimaryHolder(firstRow.primary_holder_name || '');
      setSecondaryHolder(firstRow.secondary_holder_name || '');
    }
  }, [insuranceData]);

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={'SUNLIFE'} Privilege={Privilege} clientData={clientData} fileName={titleName} printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
          <style>{`.canvas-sign-data img { border:0!important;} .canvas-sign-data div {font-size:12px;line-height:14px;}`}</style>
          <div className="box">
              <p className="sunlife-title">
                  <img className="fright" src="/assets/images/basic/sunlife.png" />
                  Consent and Authorization for Electronic Claims<br />
                  Submission and Assignment of Benefits Form
              </p>
          </div>
          <div className="box sunlife-consent pt2">
              <p className="fwb">Important Note:</p>
              <ol>
                  <li>Plan Members must complete this form, even if your patient or customer is the Plan Member's dependent.</li>
                  <li>We refer to the Provider as "you" or "Provider".</li>
                  <li>You keep the completed form on file for verification purposes for the later of (i) 6 years following the last service date or (ii) such longer period as required by applicable law or the regulations and professional standards of your regulatory body, college or association.</li>
              </ol>
              <p><strong>Plan</strong> means a group benefits plan or an individual policy of insurance.</p>
              <p><strong>Plan Member</strong> means for group plans, the eligible member (e.g. for an employer group plan, this is the employee) responsible for the group benefits coverage. For individual insurance policies, the Plan Member is the policyholder. Plan Members' dependents are eligible for coverage. Dependents are the Plan Member's spouse or children.</p>
              <p><strong>Plan Sponsor</strong> is the policyholder of an insured or self-insured group benefits plan. For example, for Plans covering employees, the Plan Sponsor is the Plan Member's employer.</p>
              <p><strong>Provider</strong> refers to licensed or qualified paramedical practitioner, ophthalmologist or optometrist providing medical services or goods. A Provider may be:</p>
              <ol>
                  <li>an organization, such as a facility or clinic, submitting claims on behalf of one or more healthcare practitioners; or</li>
                  <li>an individual responsible for their own billing.</li>
              </ol>
              <p>In the Sun Life Electronic Claims Submission Agreement, we refer to the above as a Healthcare Practice and Independent Healthcare Provider respectively.</p>
          </div>
          <table className="sunlife-table2">
              <tr>
                  <td className="page-col5 sunlife-br">1</td>
                  <td className="page-col95 pl1">Plan Member information</td>
              </tr>
          </table>
          <table className="sunlife-table1 mb2">
              <tr>
                  <td>
                      <div className="sunlife-table3 flwrap">
                          <div className="fleft page-col50 lab-right">
                              <span>First name</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_first_name} {clientData.client_middle_name}</span>
                          </div>
                          <div className="fleft page-col50">
                              <span>Last name</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_last_name}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3 flwrap">
                          <div className="fleft page-col80 lab-right">
                              <span>Address (street number and name)</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_address}</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Apartment or suite</span>
                              <span className="sunlife-filled" contentEditable style={{ minWidth: '40px' }}>&nbsp;</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col60 lab-right">
                              <span>City</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.city_name}</span>
                          </div>
                          <div className="fleft page-col20 lab-right">
                              <span>Province</span>
                              <span className="sunlife-filled" contentEditable>AB</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Postal Code</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.location_postal}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col50 lab-right">
                              <span>Phone Number</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_phone}</span>
                          </div>
                          <div className="fleft page-col50">
                              <span>Email Address</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_email}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col50 lab-right">
                              <span>Contract number</span>
                              <span className="sunlife-filled" contentEditable>{policyNumber ? policyNumber : groupNumber}&nbsp;</span>
                          </div>
                          <div className="fleft page-col50">
                              <span>Member id</span>
                              <span className="sunlife-filled" contentEditable>{memberId}&nbsp;</span>
                          </div>
                      </div>
                  </td>
              </tr>
          </table>
          <table className="sunlife-table2">
              <tr>
                  <td className="page-col5 sunlife-br">2</td>
                  <td className="page-col95 pl1">Patient or Customer information (Complete this if the patient or customer is not the Plan Member)</td>
              </tr>
          </table>
          <table className="sunlife-table1 mb2">
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col50 lab-right">
                              <span>First name</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_first_name} {clientData.client_middle_name}</span>
                          </div>
                          <div className="fleft page-col50">
                              <span>Last name</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_last_name}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col80 lab-right">
                              <span>Address (street number and name)</span>
                              <span className="sunlife-filled" contentEditable>{clientData.client_address}</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Apartment or suite</span>
                              <span className="sunlife-filled" contentEditable style={{ minWidth: '40px' }}>&nbsp;</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col60 lab-right">
                              <span>City</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.city_name}</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Province</span>
                              <span className="sunlife-filled" contentEditable>AB</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Postal Code</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.location_postal}</span>
                          </div>
                      </div>
                  </td>
              </tr>
          </table>
          <table className="sunlife-table2">
              <tr>
                  <td className="page-col5 sunlife-br">3</td>
                  <td className="page-col95 pl1">Provider/facility information</td>
              </tr>
          </table>
          <table className="sunlife-table1 mb2">
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="box">
                              <span className="pl1">Name</span>
                              <span className="sunlife-filled" contentEditable>{companyName}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col80 lab-right">
                              <span>Address (street number and name)</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.location_address}</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Apartment or suite</span>
                              <span className="sunlife-filled" contentEditable style={{ minWidth: '40px' }}>&nbsp;</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col60 lab-right">
                              <span>City</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.city_name}</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Province</span>
                              <span className="sunlife-filled" contentEditable>AB</span>
                          </div>
                          <div className="fleft page-col20">
                              <span>Postal Code</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.location_postal}</span>
                          </div>
                      </div>
                  </td>
              </tr>
              <tr>
                  <td>
                      <div className="sunlife-table3">
                          <div className="fleft page-col35 lab-right">
                              <span>Phone Number</span>
                              <span className="sunlife-filled" contentEditable>{companyData?.company_phone}</span>
                          </div>
                          <div className="fleft page-col35 lab-right">
                              <span>Email Address</span>
                              <span className="sunlife-filled" contentEditable>{locationData?.location_email}</span>
                          </div>
                          <div className="fleft page-col30">
                              <span>License Number</span>
                              <span className="sunlife-filled" contentEditable>&nbsp;</span>
                          </div>
                      </div>
                  </td>
              </tr>
          </table>

          <table className="sunlife-table2">
              <tr>
                  <td className="page-col5 sunlife-br">4</td>
                  <td className="page-col95 pl1">Consent and Authorization for Electronic Claims Submissions</td>
              </tr>
          </table>

          <div className="box sunlife-consent">
              <p>I authorize Provider to:</p>
              <ol>
                  <li>
                      electronically submit claims (eClaims) for healthcare goods, supplies or services for me or my dependent(s) to Sun Life Assurance Company of Canada (Sun Life) on my behalf and on behalf of my dependents
                      <ol>
                          <li>for the purposes set out below (see The Purposes) and</li>
                          <li>to the relevant parties also set out below (see Relevant Parties).</li>
                      </ol>
                  </li>
                  <li>disclose information about the e-claim (including personal health information in the Provider's files) to Sun Life. any eClaims made on behalf of my dependents and for the purposes set out in this form, I confirm that my dependents authorized me to consent to the disclosure of their personal information to Sun Life.</li>
              </ol>
            
              <p className="fwb">The Purposes</p>
              <p>I consent and agree that Sun Life and its reinsurers may collect, use and disclose the eClaims information to:</p>
              <ol>
                  <li>adjudicate, review and audit eClaims;</li>
                  <li>investigate any suspect claims involving potential fraud or plan abuse ("suspect claims"); and</li>
                  <li>underwrite and administer the Plan</li>
              </ol>
              <p> For suspect claims, I consent and agree that Sun Life and its reinsurers may also investigate claims to assess, detect and prevent potential fraud or plan abuse.</p>
              
              <p className="fwb">Relevant parties</p>
              <p>I also consent and agree that Sun Life and its reinsurers may collect, use and disclose the eClaims information with relevant parties. These parties include persons or organizations having relevant information and a need to know about the eClaim including:</p>
              <ol>
                  <li>the Provider or other health practitioners;</li>
                  <li>clinics, facilities, hospitals or other institutions; and</li>
                  <li>other insurers.</li>
              </ol>
              <p>For suspect claims, I further consent and agree that Sun Life and its reinsurers may collect, use and disclose eClaims information with relevant parties that include:</p>
              <ol>
                  <li>investigative agencies and the police</li>
                  <li>regulatory bodies or associations</li>
                  <li>government organizations</li>
                  <li>medical suppliers</li>
                  <li>other insurers</li>
                  <li>my Plan Sponsor.</li>
              </ol>
              
              <p className="fwb">Overpayments</p>
              <p>If there is an overpayment, I authorize:</p>
              <ol>
                  <li>the recovery of the full amount of the overpayment from any amount payable to me under the Plan; and</li>
                  <li>Sun Life to collect, use and disclose information about the eClaims with collection agencies.</li>
              </ol>
            
              <p className="fwb">General Information</p>
              <p>I also understand that information pertaining to eClaims may be reviewed if the Plan is audited.</p>
              <p>Any reference to Sun Life, reinsurers or the Plan Sponsor includes their agents and service providers.</p>
              <p>A photocopy or electronic version of this authorization is as valid as the original, and remains in effect for the continued administration of the Plan.</p>
              
              <table className="sunlife-table1 mb2">
                  <tr>
                      <td>
                          <div className="sunlife-table3">
                              <div className="fl col6 lab-right pl2">
                                  <SignaturePad signData={signData} setSignData={setSignData} receiverName={receiverName} setReceiverName={setReceiverName} receiverPhone={receiverPhone} setReceiverPhone={setReceiverPhone} />
                              </div>
                              <div className="fl col4">
                                  <span>Date (dd-mm-yyyy)</span>
                                  <span className="sunlife-filled" contentEditable>{Utility.Format.Date(locateDate,"dd-MM-yyyy")}</span>
                              </div>
                          </div>
                      </td>
                  </tr>
                  {/* <tr>
                      <td>
                          <div className="sunlife-table3">
                              <div className="box">
                                  <span className="pl1">Print Name</span>
                                  <span className="sunlife-filled" contentEditable>&nbsp;</span>
                              </div>
                          </div>
                      </td>
                  </tr> */}
              </table>
                              
              <table className="sunlife-table2">
                  <tr>
                      <td className="page-col5 sunlife-br">5</td>
                      <td className="page-col95 pl1">Assignment of Benefits</td>
                  </tr>
              </table>
              
              <p>I assign the benefits payable for my and/or my dependents' eClaims to the Provider.</p>
              <p>I authorize Sun Life to issue payment directly to Provider.</p>
              <p>I understand that:</p>
              <ol>
                  <li>I'm responsible for payment to Provider should Sun Life decline this eClaim.</li>
                  <li>Sun Life is not required to accept this assignment.</li>
                  <li>Sun Life's payment, whether to Provider or me, will discharge Sun Life's obligation under the Plan.</li>
              </ol>
              <p>This Assignment will apply to all eligible eClaims Provider submits electronically on my behalf until I revoke it in writing with reasonable notice to Sun Life.</p>
              <p>A photocopy or electronic version of this Assignment will be as valid as the original. This Assignment may remain in effect for the continued administration of the Plan.</p>


              <table className="sunlife-table1 mb2">
                  <tr>
                      <td>
                          <div className="sunlife-table3">
                              <div className="fl col6 lab-right pl2">
                                  <SignaturePad signData={signData} setSignData={setSignData} receiverName={receiverName} setReceiverName={setReceiverName} receiverPhone={receiverPhone} setReceiverPhone={setReceiverPhone} />
                              </div>
                              <div className="fl col4">
                                  <span>Date (dd-mm-yyyy)</span>
                                  <span className="sunlife-filled" contentEditable>{Utility.Format.Date(locateDate,"dd-MM-yyyy")}</span>
                              </div>
                          </div>
                      </td>
                  </tr>
                  {/* <tr>
                      <td>
                          <div className="sunlife-table3">
                              <div className="box">
                                  <span className="pl1">Print Name</span>
                                  <span className="sunlife-filled" contentEditable>&nbsp;</span>
                              </div>
                          </div>
                      </td>
                  </tr> */}
              </table>

              <table className="sunlife-table2">
                  <tr>
                      <td className="page-col5 sunlife-br">6</td>
                      <td className="page-col95 pl1">Respecting your privacy</td>
                  </tr>
              </table>

              <p>Respecting your privacy is a priority for the Sun Life group of companies. We keep in confidence personal information about you and the products and services you have with us to provide you with investment, retirement and insurance products and services to help you meet your lifetime financial objectives. To meet these objectives, we collect, use and disclose your personal information for purposes that include: underwriting; administration; claims adjudication; protecting against fraud, errors or misrepresentations; meeting legal, regulatory or contractual requirements; and we may tell you about other related products and services that we believe meet your changing needs. The only people who have access to your personal information are our employees, distribution partners such as advisors, and third-party service providers, along with our reinsurers. We will also provide access to anyone else you authorize. Sometimes, unless we are otherwise prohibited, these people may be in countries outside Canada, so your personal information may be subject to the laws of those countries. You can ask for the information in our files about you and, if necessary, ask us in writing to correct it. To find out more about our privacy practices, visit www.sunlife.ca/privacy.</p>
              <p><strong>Questions?</strong> Please visit www.sunlife.ca or call our toll-free number 1-800-361-6212 any business day from 8 a.m. to 8 p.m. ET.</p>
          </div>
          {/* <Field.PrintedOn /> */}
        </Control.Print.Data>    
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default ConsentPrint17;